import type { CreateCanonicalCoachOnboardingCommand } from "@billiard/contracts";
import { isCreateCanonicalCoachOnboardingCommand } from "@billiard/contracts";

export const CANONICAL_COACH_ONBOARDING_RECOVERY_STORAGE_KEY =
  "billiard-erp:canonical-coach-onboarding-recovery:v1";

export type CanonicalCoachOnboardingRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface CanonicalCoachOnboardingRecovery {
  ownerIdentityKey: string;
  command: CreateCanonicalCoachOnboardingCommand;
  state: CanonicalCoachOnboardingRecoveryState;
}

export type CanonicalCoachOnboardingRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: CanonicalCoachOnboardingRecovery }
  | { status: "OTHER_IDENTITY" }
  | { status: "CONFLICT" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

let pendingRecovery: CanonicalCoachOnboardingRecovery | null = null;
let dispatchingRecovery: CanonicalCoachOnboardingRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;

export function canonicalCoachOnboardingIdentityKey(identity: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([identity.tenantId, identity.storeId, identity.id]);
}

function identityKeyIsValid(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 1_024) return false;
  try {
    const parts = JSON.parse(value) as unknown;
    return (
      Array.isArray(parts) &&
      parts.length === 3 &&
      parts.every((part) => typeof part === "string" && part.length > 0 && part.length <= 256)
    );
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

export function canonicalCoachOnboardingCommandsMatch(
  left: CreateCanonicalCoachOnboardingCommand,
  right: CreateCanonicalCoachOnboardingCommand,
): boolean {
  return (
    left.profileId === right.profileId &&
    left.coachNumber === right.coachNumber &&
    left.name === right.name &&
    left.displayName === right.displayName &&
    left.controlledPhone === right.controlledPhone &&
    left.avatarUrl === right.avatarUrl &&
    left.levelId === right.levelId &&
    left.expectedCoachConfigurationVersion === right.expectedCoachConfigurationVersion &&
    left.reason === right.reason &&
    left.idempotencyKey === right.idempotencyKey
  );
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: CreateCanonicalCoachOnboardingCommand,
  state: CanonicalCoachOnboardingRecoveryState,
): CanonicalCoachOnboardingRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function parseStored(value: unknown): CanonicalCoachOnboardingRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["recovery", "version"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery)
  ) {
    return null;
  }
  const recovery = value.recovery;
  if (
    !hasExactKeys(recovery, ["command", "ownerIdentityKey", "state"]) ||
    !identityKeyIsValid(recovery.ownerIdentityKey) ||
    !isCreateCanonicalCoachOnboardingCommand(recovery.command) ||
    (recovery.state !== "CLAIMED" && recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(recovery.ownerIdentityKey, recovery.command, recovery.state);
}

function recoveryMatches(
  left: CanonicalCoachOnboardingRecovery,
  right: CanonicalCoachOnboardingRecovery,
): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    canonicalCoachOnboardingCommandsMatch(left.command, right.command)
  );
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" || typeof sessionStorage === "undefined"
      ? null
      : sessionStorage;
  } catch {
    return null;
  }
}

function readStored(target: Storage): CanonicalCoachOnboardingRecovery | null {
  const encoded = target.getItem(CANONICAL_COACH_ONBOARDING_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : parseStored(JSON.parse(encoded) as unknown);
}

function saveStored(recovery: CanonicalCoachOnboardingRecovery): boolean {
  if (typeof window === "undefined") return true;
  const target = storage();
  if (!target) return false;
  try {
    target.setItem(
      CANONICAL_COACH_ONBOARDING_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const verified = readStored(target);
    return verified !== null && recoveryMatches(verified, recovery);
  } catch {
    return false;
  }
}

function verifyStored(recovery: CanonicalCoachOnboardingRecovery): boolean {
  if (typeof window === "undefined") return true;
  const target = storage();
  if (!target) return false;
  try {
    const current = readStored(target);
    return current !== null && recoveryMatches(current, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const target = storage();
  if (!target) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = target.getItem(CANONICAL_COACH_ONBOARDING_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    const stored = readStored(target);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovered =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStored(recovered)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovered;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readCanonicalCoachOnboardingRecovery(
  identityKey: string,
): CanonicalCoachOnboardingRecoveryView {
  hydrate();
  if (recoveryUnavailable || !identityKeyIsValid(identityKey)) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_IDENTITY" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimCanonicalCoachOnboardingRecovery(
  identityKey: string,
  command: CreateCanonicalCoachOnboardingCommand,
): CanonicalCoachOnboardingRecoveryView {
  const current = readCanonicalCoachOnboardingRecovery(identityKey);
  if (current.status === "OWNED") {
    return canonicalCoachOnboardingCommandsMatch(current.recovery.command, command)
      ? current
      : { status: "CONFLICT" };
  }
  if (current.status !== "NONE") return current;
  if (!identityKeyIsValid(identityKey) || !isCreateCanonicalCoachOnboardingCommand(command)) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!saveStored(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  pendingRecovery = recovery;
  return { status: "OWNED", recovery };
}

function verify(recovery: CanonicalCoachOnboardingRecovery, identityKey: string): boolean {
  hydrate();
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    !verifyStored(recovery)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function beginCanonicalCoachOnboardingDispatch(
  recovery: CanonicalCoachOnboardingRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery || !verify(recovery, identityKey)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishCanonicalCoachOnboardingDispatch(
  recovery: CanonicalCoachOnboardingRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markCanonicalCoachOnboardingOutcomeUnknown(
  recovery: CanonicalCoachOnboardingRecovery,
  identityKey: string,
): boolean {
  if (!verify(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStored(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  return true;
}

export function clearCanonicalCoachOnboardingRecovery(
  recovery: CanonicalCoachOnboardingRecovery,
  identityKey: string,
): boolean {
  hydrate();
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery.ownerIdentityKey !== identityKey ||
    !recoveryMatches(pendingRecovery, recovery)
  ) {
    return false;
  }
  if (typeof window !== "undefined") {
    const target = storage();
    if (!target) return false;
    try {
      const current = readStored(target);
      if (!current || !recoveryMatches(current, recovery)) return false;
      target.removeItem(CANONICAL_COACH_ONBOARDING_RECOVERY_STORAGE_KEY);
      if (target.getItem(CANONICAL_COACH_ONBOARDING_RECOVERY_STORAGE_KEY) !== null) return false;
    } catch {
      return false;
    }
  }
  pendingRecovery = null;
  return true;
}

export function resetCanonicalCoachOnboardingRecoveryForTests(clearStorage = true): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  if (!clearStorage) return;
  try {
    storage()?.removeItem(CANONICAL_COACH_ONBOARDING_RECOVERY_STORAGE_KEY);
  } catch {
    // Tests reset the in-memory fail-closed state even when storage is unavailable.
  }
}
